/**
 * Collection Page Cart Minimum Messaging
 *
 * Dynamically updates the cart minimum messaging on collection pages
 * based on the current cart total. Supports both one-time and subscription orders.
 *
 * Thresholds:
 * - One-time: $40 minimum order, $65 free delivery
 * - Subscription: $38 minimum order, $61 free delivery
 */

(function() {
  'use strict';

  // Gift card variant IDs to exclude from calculations
  var giftCardIds = [31478736748586, 31478736781354, 31478736814122, 31478736846890];

  /**
   * Calculate cart totals excluding gift cards
   * @param {Object} cartData - The cart data from /cart.js
   * @returns {Object} - Contains hasSubscription and originalPrice
   */
  function calculateCartTotals(cartData) {
    var hasSubscription = false;
    var originalPrice = 0;
    var items = (cartData || {}).items || [];

    items.forEach(function(item) {
      // Check if item has a selling plan (subscription)
      if (Object.keys(item.selling_plan_allocation || {}).length > 0) {
        hasSubscription = true;
      }

      // Exclude gift cards from the total
      if (!giftCardIds.includes(item.id)) {
        if (item.original_line_price !== item.final_line_price) {
          originalPrice += item.final_line_price;
        } else {
          originalPrice += item.original_line_price;
        }
      }
    });

    return {
      hasSubscription: hasSubscription,
      originalPrice: originalPrice
    };
  }

  /**
   * Get the appropriate thresholds based on subscription status
   * @param {boolean} hasSubscription - Whether cart contains subscription items
   * @returns {Object} - Contains minimumOrder and freeDelivery in cents
   */
  function getThresholds(hasSubscription) {
    if (hasSubscription) {
      return {
        minimumOrder: 4000,  // $40.00
        freeDelivery: 6100   // $61.00
      };
    }
    return {
      minimumOrder: 4000,    // $40.00
      freeDelivery: 6500     // $65.00
    };
  }

  /**
   * Check if current site is US (.com domain)
   * @returns {boolean}
   */
  function isUS() {
    return window.location.hostname.endsWith('.com');
  }

  function isUSAEast() {
    return window.location.pathname.toLowerCase().indexOf('/collections/usaeast') !== -1;
  }

  /**
   * Generate the appropriate message based on cart state
   * @param {number} cartTotal - Cart total in cents
   * @param {Object} thresholds - The minimumOrder and freeDelivery thresholds
   * @returns {string} - The message to display (HTML formatted)
   */
  function generateMessage(cartTotal, thresholds) {
    // USA East: use $40 min / $70 free delivery
    if (isUSAEast()) {
      var ueMin = 4000;
      var ueFree = 7000;
      if (cartTotal < ueMin) {
        var ueNeeded = Math.ceil((ueMin - cartTotal) / 100);
        return 'Only <span class="red-price">$' + ueNeeded + '</span> away from minimum order';
      } else if (cartTotal < ueFree) {
        var ueAway = Math.ceil((ueFree - cartTotal) / 100);
        return 'Only <span class="red-price">$' + ueAway + '</span> Away from Free Delivery';
      } else {
        return 'Free delivery unlocked! Click <a href="/cart">checkout</a>, when you\'re ready';
      }
    }

    var orderDifference = thresholds.minimumOrder - cartTotal;
    var deliveryDifference = thresholds.freeDelivery - cartTotal;

    if (orderDifference > 0) {
      // Below minimum order
      var amountNeeded = Math.ceil(orderDifference / 100);
      return 'Only <span class="red-price">$' + amountNeeded + '</span> away from minimum order';
    } else if (isUS()) {
      // US: No free delivery, just show minimum reached message
      return 'Minimum reached! Click <a href="/cart">checkout</a>, when you\'re ready';
    } else if (deliveryDifference > 0) {
      // Non-US: Met minimum, but not free delivery
      var amountForFreeDelivery = Math.ceil(deliveryDifference / 100);
      return 'Only <span class="red-price">$' + amountForFreeDelivery + '</span> Away from Free Delivery';
    } else {
      // Non-US: Free delivery unlocked
      return 'Free delivery unlocked! Click <a href="/cart">checkout</a>, when you\'re ready';
    }
  }

  /**
   * Update the collection page cart minimum messaging
   * @param {Object} cartData - The cart data from /cart.js
   */
  function updateCollectionCartMinimum(cartData) {
    var messageElement = document.getElementById('collection-cart-minimum-text');
    if (!messageElement) return;

    var totals = calculateCartTotals(cartData);
    var thresholds = getThresholds(totals.hasSubscription);
    var message = generateMessage(totals.originalPrice, thresholds);

    messageElement.innerHTML = message;
  }

  // Expose globally so global.js can call it directly with cart data
  window.updateCollectionCartMinimum = updateCollectionCartMinimum;

  /**
   * Fetch cart data with 429 retry, then update the messaging
   */
  function fetchAndUpdateMessage() {
    (async function() {
      for (var attempt = 0; attempt < 3; attempt++) {
        try {
          var response = await fetch('/cart.js', {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
          });
          if (response.status === 429) {
            await new Promise(function(r) { setTimeout(r, 1500 * (attempt + 1)); });
            continue;
          }
          if (!response.ok) throw new Error('Cart API error: ' + response.status);
          var cartData = await response.json();
          updateCollectionCartMinimum(cartData);
          return;
        } catch (error) {
          if (attempt === 2) {
            console.error('Collection cart minimum: Error fetching cart', error);
          }
        }
      }
    })();
  }

  /**
   * Initialize the collection cart minimum messaging
   */
  function init() {
    // Check if the element exists on this page
    if (!document.getElementById('collection-cart-minimum-text')) return;

    // Initial update on page load
    fetchAndUpdateMessage();

    // Listen for cart updates via the existing cartUpdatedZapiet event
    window.addEventListener('cartUpdatedZapiet', fetchAndUpdateMessage);

    // Also listen for standard Shopify cart updates - use event data if available
    document.addEventListener('cart:updated', function(event) {
      if (event.detail) {
        // Cart data provided in event - use it directly (no fetch needed)
        updateCollectionCartMinimum(event.detail);
      } else {
        // No cart data in event - fetch it
        fetchAndUpdateMessage();
      }
    });

    // Listen for quantity changes on the collection page
    document.addEventListener('change', function(event) {
      if (event.target.closest('quantity-input') ||
          event.target.closest('quantity-input-new') ||
          event.target.closest('.quantity-input')) {
        // Debounce the update slightly to allow cart to sync
        setTimeout(fetchAndUpdateMessage, 500);
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
